import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import Image from 'next/image';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Carousel from 'Frontend/components/Carousel';
import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {parseToUri} from 'Admin/utils/formParsers';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA, MEDIA_JS} from 'Frontend/utils/mediaQueries';

/**
 * ImageModule
 *
 * @property {*} prop Prop comment.
 *
 * @class ImageModule
 * @extends {Component}
 */
class ImageModule extends Component {
    static propTypes = {
        images: PropTypes.array.isRequired,
        isInGroup: PropTypes.bool,
        name: PropTypes.string
    }

    static defaultProps = {
        isInGroup: false,
        name: null
    }

    /**
     * Creates an instance of ImageModule.
     *
     * @param {Object} props Component props.
     * @memberof ImageModule
     */
    constructor(props) {
        super(props);

        if (process.browser) {
            this.mobile = window.matchMedia(MEDIA_JS.MOBILE.DOWN);
        }

        this.state = {viewport: 'Desktop'};
    }

    /**
     * Add listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentDidMount() {
        if ('addEventListener' in this.mobile) {
            this.mobile.addEventListener('change', this.setViewport);
        } else {
            this.mobile.addListener(this.setViewport);
        }

        this.setViewport();
    }

    /**
     * Remove listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentWillUnmount() {
        if ('removeEventListener' in this.mobile) {
            this.mobile.removeEventListener('change', this.setViewport);
        } else {
            this.mobile.removeListener(this.setViewport);
        }
    }

    /**
     * Sets the viewport in state.
     *
     * @memberof ImageModule
     */
    @autobind
    setViewport() {
        this.setState({viewport: this.getViewport()});
    }

    /**
     * Gets the viewport from the mediaQuerySelector.
     *
     * @returns {String} The current viewport.
     * @memberof ImageModule
     */
    @autobind
    getViewport() {
        if (this.mobile.matches) {
            return 'Mobile';
        }

        return 'Desktop';
    }

    /**
     * Renders the image part of the Accordion.
     *
     * @returns {JSX} The Image jsx.
     * @memberof ImageModule
     */
    renderImages() {
        const {images} = this.props;

        if (images.length === 0) {
            return null;
        }

        if (images.length > 1) {
            return (
                <Carousel>
                    {images.map(img => {
                        const {alt, dimensions: {height, width}, publicPath, title} = img;

                        return (
                            <Image
                                key={publicPath.src}
                                alt={alt}
                                height={height}
                                layout="responsive"
                                src={publicPath.src}
                                title={title}
                                width={width}
                            />
                        );
                    })}
                </Carousel>
            );
        }

        const {alt, dimensions: {height, width}, publicPath, title} = images[0];

        return (
            <Image
                alt={alt}
                height={height}
                layout="responsive"
                src={publicPath.src}
                title={title}
                width={width}
            />
        );
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof ImageModule
     */
    render() {
        const {viewport} = this.state;
        const {images, isInGroup, name} = this.props;

        return (
            <ImageModuleElement isInGroup={isInGroup}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                {(viewport === 'Desktop') && (
                    <>
                        {(images.length === 1) && (
                            <SingleImage>
                                <Image
                                    alt={images[0].alt}
                                    layout="fill"
                                    objectFit="cover"
                                    objectPosition="center center"
                                    src={images[0].publicPath.src}
                                    title={images[0].title}
                                />
                            </SingleImage>
                        )}
                        {/* eslint-disable-next-line @nfq/no-magic-numbers */}
                        {(images.length === 2) && (
                            <DoubleImages isInGroup={isInGroup}>
                                <MediumImage>
                                    <Image
                                        alt={images[0].alt}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center center"
                                        src={images[0].publicPath.src}
                                        title={images[0].title}
                                    />
                                </MediumImage>
                                <BigImage>
                                    <Image
                                        alt={images[1].alt}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center center"
                                        src={images[1].publicPath.src}
                                        title={images[1].title}
                                    />
                                </BigImage>
                            </DoubleImages>
                        )}
                        {/* eslint-disable-next-line @nfq/no-magic-numbers */}
                        {(images.length === 3) && (
                            <TrippleImages isInGroup={isInGroup}>
                                <SmallestImage>
                                    <Image
                                        alt={images[0].alt}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center center"
                                        src={images[0].publicPath.src}
                                        title={images[0].title}
                                    />
                                </SmallestImage>
                                <MediumImage mode="tripple">
                                    <Image
                                        alt={images[1].alt}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center center"
                                        src={images[1].publicPath.src}
                                        title={images[1].title}
                                    />
                                </MediumImage>
                                <BigImage mode="tripple">
                                    <Image
                                        alt={images[2].alt}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center center"
                                        src={images[2].publicPath.src}
                                        title={images[2].title}
                                    />
                                </BigImage>
                            </TrippleImages>
                        )}
                    </>
                )}
                {(viewport === 'Mobile') && (
                    <MobileImages>
                        {this.renderImages()}
                    </MobileImages>
                )}
            </ImageModuleElement>
        );
    }
}

export default ImageModule;

const ImageModuleElement = styled(ModuleMargin)`
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        max-width: max(1069px, ${pxToVw(1069, 'd')});
        padding: ${({isInGroup}) => (isInGroup ? '0' : `0 max(110px, ${pxToVw(110, 'd')}) 0 max(110px, ${pxToVw(110, 'd')})`)};
        width: ${({isInGroup}) => (isInGroup ? '100%' : `calc(100% - max(251px, ${pxToVw(251, 'd')}))`)};
    }
`;

const SingleImage = styled.div`
    height: ${clampCss(301, 't')};
    margin: auto;
    position: relative;
    width: ${clampCss(377, 't')};

    ${MEDIA.DESKTOP.UP} {
        height: max(502px, ${pxToVw(502, 'd')});
        width: max(629px, ${pxToVw(629, 'd')});
    }
`;

const DoubleImages = styled.div`
    display: grid;
    gap: ${clampCss(16, 't')} ${clampCss(16, 't')};
    grid-template-columns: ${({isInGroup}) => (isInGroup ? '56fr 44fr' : '55fr 45fr')};
    grid-template-rows: 1fr;
    width: 100%;

    ${MEDIA.DESKTOP.UP} {
        gap: max(16px, ${pxToVw(16, 'd')}) max(16px, ${pxToVw(16, 'd')});
        grid-template-columns: ${({isInGroup}) => (isInGroup ? '56fr 44fr' : '58fr 42fr')};
        grid-template-rows: 1fr;
    }
`;

const TrippleImages = styled.div`
    display: grid;
    gap: ${clampCss(16, 't')} ${clampCss(16, 't')};
    grid-template-columns: ${({isInGroup}) => (isInGroup ? '46fr 54fr' : '45fr 55fr')};
    grid-template-rows: 42fr 58fr;
    width: 100%;

    ${MEDIA.DESKTOP.UP} {
        gap: max(16px, ${pxToVw(16, 'd')}) max(16px, ${pxToVw(16, 'd')});
        grid-template-columns: ${({isInGroup}) => (isInGroup ? '44fr 56fr' : '42fr 58fr')};
        grid-template-rows: 42fr 58fr;
    }
`;

const SmallestImage = styled.div`
    grid-column: 1 / 1;
    grid-row: 1 / 1;
    height: ${clampCss(129, 't')};
    justify-self: end;
    position: relative;
    width: ${clampCss(162, 't')};

    ${MEDIA.DESKTOP.UP} {
        height: max(214px, ${pxToVw(214, 'd')});
        width: max(268px, ${pxToVw(268, 'd')});
    }
`;

const MediumImage = styled.div`
    align-self: center;
    grid-column: ${({mode}) => (mode === 'tripple' ? '1 / 1' : '2 / 2')};
    grid-row: ${({mode}) => (mode === 'tripple' ? '2 / 2' : '1 / 1')};
    height: ${clampCss(172, 't')};
    justify-self: ${({mode}) => (mode === 'tripple' ? 'end' : 'start')};
    max-width: 100%;
    position: relative;
    width: ${clampCss(215, 't')};

    ${MEDIA.DESKTOP.UP} {
        height: max(286px, ${pxToVw(286, 'd')});
        width: max(358px, ${pxToVw(358, 'd')});
    }
`;

const BigImage = styled.div`
    align-self: center;
    grid-column: ${({mode}) => (mode === 'tripple' ? '2 / 2' : '1 / 1')};
    grid-row: ${({mode}) => (mode === 'tripple' ? '1 / 3' : '1 / 1')};
    height: ${clampCss(230, 't')};
    justify-self: ${({mode}) => (mode === 'tripple' ? 'start' : 'end')};
    position: relative;
    width: ${clampCss(287, 't')};

    ${MEDIA.DESKTOP.UP} {
        height: max(381px, ${pxToVw(381, 'd')});
        width: max(477px, ${pxToVw(477, 'd')});
    }
`;

const MobileImages = styled.div`
    width: 100%;

    ${MEDIA.TABLET.UP} {
        align-self: flex-start;
        margin-right: 32px;
        width: calc(60% - 16px);
    }
`;